'use client';

import { useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { usePermissions } from '@/features/auth/hooks/use-permissions';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { ROUTE_PERMISSIONS } from '@/shared/components/route-permissions';

/**
 * Guard centralizat de permisiuni pe rută. Montat O DATĂ în dashboard layout.
 *
 * Dacă ruta curentă cere o permisiune (vezi ROUTE_PERMISSIONS) pe care rolul
 * userului NU o are → toast + redirect către o pagină accesibilă (evită pagina
 * ruptă cu 403-uri în consolă pt rolurile limitate). Complementar cu tag-urile
 * `permission` din sidebar (care ascund tab-ul) și cu <RoleRouteGuard> per-pagină.
 *
 * Match pe cel mai LUNG prefix (ex. /stocuri/lichidare → stock.adjust, nu stock.read).
 * Redirect-ul evită bucla: /facturi (neguarded) dacă userul are invoices.read, altfel /setari.
 */
export function RoutePermissionGuard() {
  const router = useRouter();
  const pathname = usePathname() || '';
  const { can } = usePermissions();
  const isLoading = useAuthStore((s) => s.isLoading);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const role = useAuthStore((s) => s.user?.role);

  useEffect(() => {
    if (isLoading || !isAuthenticated || !role) return;
    const match = ROUTE_PERMISSIONS
      .filter((r) => pathname === r.prefix || pathname.startsWith(r.prefix + '/'))
      .sort((a, b) => b.prefix.length - a.prefix.length)[0];
    if (!match || can(match.permission)) return;
    toast.error('Nu ai acces la această secțiune.');
    router.replace(can('invoices.read') ? '/facturi' : '/setari');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, isLoading, isAuthenticated, role, can, router]);

  return null;
}
